import React, { Suspense, lazy, useMemo } from "react";
import RoleHeader from "components/app/authorization/role/RoleHeader";
import { Card, Tabs, Alert, LoadingSpinner } from "components/common";
import { useSelector } from "react-redux";
import { FaList, FaUserCog, FaUserShield } from "react-icons/fa";

const AllList = lazy(() => import('components/app/authorization/role/RoleList'));
const RoleByUsersList = lazy(() => import('components/app/authorization/role/list/RoleByUsers'));
const RoleByPermissionsList = lazy(() => import('components/app/authorization/role/list/RoleByPermissions'));

// Loading fallback component
const TabLoadingFallback = React.memo(() => (
  <div className="tw-text-center tw-py-12">
    <LoadingSpinner message="Loading..." />
  </div>
));

TabLoadingFallback.displayName = 'TabLoadingFallback';

const RolePage = () => {
  // The List tab (RoleList) owns fetching `allData` from its persisted
  // `currentParams` (which carries the search filter), so we must NOT refetch
  // here with an unfiltered {page, per_page} — that clobbered the filter when
  // navigating to a role and back. Just read shared loading/error for the banner.
  const { loading, error } = useSelector((state) => state.authorization_role);

  // Memoized tab items for better performance
  const tabItems = useMemo(() => [
    {
      key: 'role-list',
      icon: FaList,
      label: 'List',
      component: AllList
    },
    {
      key: 'role-by-user',
      icon: FaUserShield,
      label: 'Role by Users',
      component: RoleByUsersList
    },
    {
      key: 'role-by-permission',
      icon: FaUserCog,
      label: 'Role by Permissions',
      component: RoleByPermissionsList
    }
  ], []);

  const tabs = useMemo(() => tabItems.map(({ key, icon: Icon, label, component: Component }) => ({
    id: key,
    label,
    icon: <Icon size={16} />,
    content: (
      <Suspense fallback={<TabLoadingFallback />}>
        <Component />
      </Suspense>
    )
  })), [tabItems]);

  return (
    <>
      <RoleHeader />

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error Loading Roles" className="tw-mb-4">
          {error?.message || 'An error occurred while loading roles'}
        </Alert>
      )}

      {/* Global Loading State */}
      {loading && (
        <div className="tw-text-center tw-my-6">
          <LoadingSpinner message="Loading roles..." />
        </div>
      )}

      <Card className="tw-shadow-sm" padding="md">
        <Tabs tabs={tabs} lazyMount />
      </Card>
    </>
  );
};

export default React.memo(RolePage);
